const _ = require('lodash');
const EventNames = require('@/lib/Events/EventNames');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const OnlineTaskType = require('@/lib/OnlineQueue/OnlineTaskType');
const { formatDateForApi } = require('@/lib/Dates/dateFunctions');
const { toBool, toDate } = require('@/functions/transform');
const { updateAngularUi } = require('@/functions/angular');

class RedemptionService {
    /**
     * @param {CurrentDevice} currentDevice
     * @param {EventBus} eventBus
     * @param {OnlineQueue} onlineQueue
     * @param {OrderRepository} orderRepository
     * @param redemptionResource
     */
    constructor(
        currentDevice,
        eventBus,
        onlineQueue,
        orderRepository,
        redemptionResource
    ) {
        this.currentDevice = currentDevice;
        this.eventBus = eventBus;
        this.onlineQueue = onlineQueue;
        this.orderRepository = orderRepository;
        this.redemptionResource = redemptionResource;

        this.logger = logFactory('RedemptionService');

        /**
         * @type {Object<function>}
         */
        this.debouncedDoAdjustRedemptionCounts = {};

        /**
         * @type {Function}
         */
        this.broadcastTicketRedemptionActivity = _.debounce(
            () => eventBus.broadcast(EventNames.TICKET_REDEMPTION_ACTIVITY),
            200
        );
    }

    getDefaultRedemptionData() {
        return {
            deviceID: this.currentDevice.getDeviceId(),
            triggeredAt: formatDateForApi(new Date()),
            tradingSessionID: this.currentDevice.getTradingSessionID(),
        };
    }

    /**
     * Immediately (not debounced) notify that redemption activity has started, so Vue can pick up
     * transient state (e.g. an order line's redemptionInProgress/unredemptionInProgress flags)
     * right away, rather than waiting for the debounced "settled" broadcast in postRedemption(),
     * which by definition only fires once the in-progress flag has already been cleared again.
     */
    notifyRedemptionActivity() {
        this.eventBus.broadcast(EventNames.TICKET_REDEMPTION_ACTIVITY);
    }

    /**
     * @private
     *
     * Add a redemption request to the queue
     *
     * @param {string|number} refOrID
     * @param {number} qty
     * @param {string} method
     * @param {string} type
     *
     * @returns {Promise<OnlineTask>}
     */
    addRedemptionToQueue(refOrID, qty, method, type) {
        return this.onlineQueue.addTask(
            this.onlineQueue.createTask(
                OnlineTaskType.REDEMPTION,
                {
                    ...this.getDefaultRedemptionData(),
                    ref: refOrID,
                    qty,
                    method,
                    type,
                },
                `Redeem ${qty} x ${type.toLowerCase()} ${refOrID}.`
            )
        );
    }

    /**
     * Try to redeem the order line via the API
     *
     * @param {{orderLineID: number, qty: number, method: string, tradingSessionID: number, onlineTaskTrigger: string}} data
     *
     * @returns {Promise}
     */
    doRemoteRedemption(data) {
        if (!data.type) {
            data.type = 'OrderLine';
            // orderLineID is sent as 'ref', and this also includes BC because PRO-1666 changes
            // from storing the whole orderLine in the task data to just the orderLineID.
            // TODO: This can be removed soon after PRO-1666 is released.
            if (!data.ref && data.orderLine) {
                data.ref = data.orderLine.ID;
            }
            delete data.orderLine;
        }

        return new Promise((resolve, reject) => {
            this.redemptionResource.redeem(
                {
                    onlineTaskTrigger: data.onlineTaskTrigger,
                },
                data,
                (successResult) => {
                    this.logger.debug('RedemptionService.doRemoteRedemption - Success',
                        {
                            data,
                            response: successResult,
                        });
                    resolve(successResult);
                },
                (failureResult) => {
                    this.logger.error('RedemptionService.doRemoteRedemption - Failed',
                        {
                            data,
                            response: failureResult,
                        });
                    reject(failureResult);
                }
            );
        });
    }

    /**
     * @private
     *
     * Do some final tasks after redemption:
     * - Log
     * - Broadcast an event
     *
     * @param {boolean} wasOnline
     * @param {Order} order
     * @param {string} description
     * @param {number} amount
     */
    postRedemption(wasOnline, order, description, amount) {
        const action = amount > 0 ? 'Redeemed' : 'Unredeemed';
        this.logger.debug(`${action} ${amount}x ${description}`);
        this.broadcastTicketRedemptionActivity();
    }

    /**
     * @private
     *
     * @param {Order} order
     * @param {OrderLine} orderLine
     * @param {number} deltaAmount
     * @param {string} method
     */
    doAdjustRedemptionCount(order, orderLine, deltaAmount, method) {
        const type = 'OrderLine';

        if (deltaAmount > 0) {
            orderLine.setRedemptionInProgress(true);
        } else if (deltaAmount < 0) {
            orderLine.setUnredemptionInProgress(true);
        }

        return this.addRedemptionToQueue(orderLine.ID, deltaAmount, method, type)
            .then((task) => {
                // Has the task actually been run instead of just being queued?
                // This does not necessarily mean it was successful, it just means it made it to the API!
                if (task.hasRun()) {
                    if (task.result && task.result.success) {
                        // (response should contain the current *live* redemption amount, so use it)
                        orderLine.redeemed = task.result.newRedemptionCount;
                        if (orderLine.item.printTicketPerInstance) {
                            // We only redeem instances automatically when there is only 1 per line.
                            orderLine.iteminstances[0].setNetRedemptions(task.result.newRedemptionCount);
                        }

                        updateOrderRedemptionStatusFromApiResponse(order, task.result);

                        this.logger.debug(
                            'Redemption submitted successfully',
                            {
                                ref: orderLine.ID,
                                qty: deltaAmount,
                                method,
                                response: task.result,
                                type,
                            }
                        );

                        orderLine.setCurrentRedeemed(true);
                    } else {
                        orderLine.setRedemptionError(true);
                    }

                    // The redemption buttons on screen can now be re-enabled.
                    orderLine.clearRedemptionInProgress();

                    updateAngularUi();
                } else {
                    // Redemption is happening offline, so just mock that the redemption was a success.
                    // This happens after a small delay if offline because without it it looks suspiciously fast
                    // like nothing happened (because it hasn't yet - it's been queued).
                    setTimeout(() => {
                        orderLine.redeemed += deltaAmount;

                        // What about updating the order redeemed/redemptionDate while offline?
                        // We'd need our own logic to decide if it is redeemed as we can't just be told by
                        // the API.
                        // Instead what we will do is set order.redeemed to null then
                        // the status will be hidden from the UI. Better to show nothing than an incorrect
                        // value!
                        order.redeemed = null;

                        // The redemption buttons on screen can now be re-enabled.
                        orderLine.clearRedemptionInProgress();
                        orderLine.setCurrentRedeemed(true);
                        this.orderRepository.persist(order);

                        updateAngularUi();
                    }, 100);
                }

                this.postRedemption(
                    task.hasRun(),
                    order,
                    `order line ${orderLine.ID}`,
                    deltaAmount
                );
            });
    }

    /**
     * @private
     *
     * @param orderLineID
     * @param params
     */
    doAdjustRedemptionCountDebounced(orderLineID, params) {
        if (!this.debouncedDoAdjustRedemptionCounts.hasOwnProperty(orderLineID)) {
            // Create a debounced doAdjustRedemptionCount for this order line.
            this.debouncedDoAdjustRedemptionCounts[orderLineID] = _.debounce(
                this.doAdjustRedemptionCount.bind(this),
                100
            );
        }
        this.debouncedDoAdjustRedemptionCounts[orderLineID](...params);
    }

    /**
     * Public - Update the redemption count on an order line
     *
     * @param {Order} order
     * @param {OrderLine} orderLine
     * @param {number} deltaAmount
     * @param {string} method
     */
    adjustRedemptionCount(order, orderLine, deltaAmount, method) {
        // Set the new amount optimistically on the order line (provide instant response to the UI).
        orderLine.setCurrentRedeemed(false);
        // Create a debounce for each line of the order.
        this.doAdjustRedemptionCountDebounced(orderLine.ID, [order, orderLine, deltaAmount, method]);
    }

    /**
     * (Un)redeem a single item instance.
     *
     * @param {Order} order
     * @param {ItemInstance} itemInstance
     * @param {Number} qty
     * @param {boolean} [notify] Broadcast that redemption activity has started immediately.
     *   Pass false when called as part of a batch (e.g. TicketRedeemer.redeemLines) which will
     *   broadcast once itself after dispatching the whole batch, rather than once per instance.
     */
    redeemItemInstance(order, itemInstance, qty, notify = true) {
        if (qty > 0) {
            itemInstance.setRedemptionInProgress();
        } else if (qty < 0) {
            itemInstance.setUnredemptionInProgress();
        }

        if (notify) {
            this.notifyRedemptionActivity();
        }

        return this.addRedemptionToQueue(itemInstance.itemInstanceRef, qty, null, 'iteminstance')
            .then((task) => {
                if (task.hasRun()) {
                    // Redemption happened online.
                    if (task.result && task.result.success) {
                        itemInstance.setNetRedemptions(task.result.newRedemptionCount);

                        updateOrderRedemptionStatusFromApiResponse(order, task.result);

                        this.logger.debug(
                            'Redemption submitted successfully',
                            {
                                ref: itemInstance.itemInstanceRef,
                                qty,
                                method: null,
                                response: task.result,
                                type: 'iteminstance',
                            }
                        );
                    } else {
                        itemInstance.setRedemptionError(true);
                    }

                    // The redemption buttons on screen can now be re-enabled.
                    itemInstance.clearRedemptionInProgress();

                    updateAngularUi();
                } else {
                    // Redemption is happening offline, so just mock that the redemption was a success.
                    // This happens after a small delay if offline because without it it looks suspiciously fast
                    // like nothing happened (because it hasn't yet - it's been queued).
                    setTimeout(() => {
                        itemInstance.setNetRedemptions(itemInstance.netRedemptions + qty);

                        // What about updating the order redeemed/redemptionDate while offline?
                        // We'd need our own logic to decide if it is redeemed as we can't just be told by
                        // the API.
                        // Instead what we will do is set order.redeemed to null then
                        // the status will be hidden from the UI. Better to show nothing than an incorrect
                        // value!
                        order.redeemed = null;

                        // The redemption buttons on screen can now be re-enabled.
                        itemInstance.clearRedemptionInProgress();
                        this.orderRepository.persist(order);

                        updateAngularUi();
                    }, 100);
                }

                this.postRedemption(
                    task.hasRun(),
                    order,
                    `item instance ${itemInstance.ID}`,
                    qty
                );
            });
    }
}

/**
 * If we have the order's new redemption status from the API result (provided since PRO-1719) update the order.
 *
 * @param {Order} order
 * @param {{newRedemptionCount: number, order: {redeemed: boolean, redemptionDate: Date}}} apiRedemptionResponse
 */
const updateOrderRedemptionStatusFromApiResponse = (order, apiRedemptionResponse) => {
    if (apiRedemptionResponse && apiRedemptionResponse.order) {
        order.redeemed = toBool(apiRedemptionResponse.order.redeemed);
        order.redemptionDate = toDate(apiRedemptionResponse.order.redemptionDate);
    }
};

module.exports = RedemptionService;
